import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useCustomerAuth();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords don\u2019t match'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('bf_customer_session', '1');
        await refreshAuth?.();
        toast.success('Password set — you\u2019re signed in!');
        navigate('/my-trips');
      } else {
        const detail = data.detail;
        setError(typeof detail === 'string' ? detail : 'This link is invalid or has expired.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-none bg-gray-50 rounded-3xl" data-testid="set-password-card">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Set your password</h1>
            <p className="text-sm text-gray-500">Choose a password for your BookingFlows account.</p>
          </div>

          {!token ? (
            <div className="text-center">
              <p className="text-sm text-red-600 mb-4">This link is missing its security token.</p>
              <Link to="/" className="text-sm text-blue-600 hover:underline">Back to BookingFlows</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <div className="relative">
                  <Input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="h-12 pr-11 bg-white"
                    autoFocus
                    data-testid="set-password-input"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                <Input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat your password"
                  className="h-12 bg-white"
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2" data-testid="set-password-error">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading || password.length < 8}
                className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
                data-testid="set-password-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set password & sign in'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
